import { z } from 'zod';
import { CHANNEL_NAME_MAX, DEFAULT_MAX_TRANSFER_BYTES, DISPLAY_NAME_MAX, MESSAGE_CONTENT_MAX, TRANSFER_BYTES_CEILING, } from '../constants.js';
/**
 * System settings: the limits and switches an administrator controls.
 *
 * Every value here is ENFORCED BY THE SERVER - the browser reads the same
 * settings only to fail early and to explain the rule, never as the gate. Each
 * numeric setting is bounded by a hard ceiling in `constants.ts`, which is what
 * the schemas and the database columns can actually hold.
 *
 * Nothing here is secret: `GET /api/v1/settings` returns the whole object to
 * anyone, because the registration form needs the password policy before there
 * is an account to authenticate with.
 */
/** Smallest transfer limit an administrator may set: 1 MB. */
export const MIN_TRANSFER_LIMIT_BYTES = 1024 * 1024;
/** Bounds for every numeric setting, shared by the schema and the admin form. */
export const SETTINGS_BOUNDS = {
    passwordMinLength: { min: 6, max: 128 },
    maxMessageLength: { min: 1, max: MESSAGE_CONTENT_MAX },
    maxDisplayNameLength: { min: 2, max: DISPLAY_NAME_MAX },
    maxChannelNameLength: { min: 2, max: CHANNEL_NAME_MAX },
    maxFileTransferBytes: { min: MIN_TRANSFER_LIMIT_BYTES, max: TRANSFER_BYTES_CEILING },
    fileRetentionMinutes: { min: 1, max: 10_080 },
    /** One hour to one year. */
    sessionMaxLifetimeHours: { min: 1, max: 8_760 },
    /** 0 turns the idle timeout off. */
    sessionIdleTimeoutHours: { min: 0, max: 8_760 },
    /** 0 turns the lockout off. */
    loginMaxFailedAttempts: { min: 0, max: 100 },
    loginLockoutMinutes: { min: 1, max: 1_440 },
};
/** Most domains the sign-up allow-list may hold. */
export const MAX_ALLOWED_EMAIL_DOMAINS = 50;
/**
 * A bare DNS name, lowercased: `example.com`, `corp.example.co.uk`. No `@`, no
 * wildcard - a domain allows itself, not its subdomains, so the list says
 * exactly who may sign up.
 */
export const emailDomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/, 'Not a valid domain, e.g. example.com');
const bounded = (key) => z.number().int().min(SETTINGS_BOUNDS[key].min).max(SETTINGS_BOUNDS[key].max);
const settingsShape = {
    /** When off, `POST /auth/register` refuses and the sign-up page says so. */
    allowRegistration: z.boolean(),
    passwordMinLength: bounded('passwordMinLength'),
    passwordRequireUppercase: z.boolean(),
    passwordRequireLowercase: z.boolean(),
    passwordRequireDigit: z.boolean(),
    passwordRequireSymbol: z.boolean(),
    maxMessageLength: bounded('maxMessageLength'),
    maxDisplayNameLength: bounded('maxDisplayNameLength'),
    maxChannelNameLength: bounded('maxChannelNameLength'),
    /** Enforced by the sender, the relay and the receiver alike. */
    maxFileTransferBytes: bounded('maxFileTransferBytes'),
    /** How long the server holds a shared file that has not been downloaded by everyone. */
    fileRetentionMinutes: bounded('fileRetentionMinutes'),
    allowMessageEditing: z.boolean(),
    allowMessageDeletion: z.boolean(),
    /** Lets either person in a direct chat delete it - and its history - for both of them. */
    allowDeleteChatForEveryone: z.boolean(),
    /**
     * The hard limit on a sign-in. However actively it is used, a session ends
     * this long after the person signed in, and they sign in again. Applied to
     * EXISTING sessions the moment it changes - it is measured from each
     * session's start, not stamped into it.
     */
    sessionMaxLifetimeHours: bounded('sessionMaxLifetimeHours'),
    /** A session not used for this long ends early. 0 means never. */
    sessionIdleTimeoutHours: bounded('sessionIdleTimeoutHours'),
    /** Failed sign-ins for one account name before it is locked for a while. 0 means never. */
    loginMaxFailedAttempts: bounded('loginMaxFailedAttempts'),
    loginLockoutMinutes: bounded('loginLockoutMinutes'),
    /**
     * When not empty, self-service sign-up accepts only email addresses in these
     * domains. Directory (LDAP) accounts are not affected.
     */
    allowedEmailDomains: z.array(emailDomainSchema).max(MAX_ALLOWED_EMAIL_DOMAINS),
};
export const systemSettingsSchema = z.object(settingsShape);
/**
 * Out-of-the-box values. They reproduce the behaviour from before settings
 * existed - 12-character passwords, 4,000-character messages, 100 MB transfers -
 * so an upgrade changes nothing until an administrator decides otherwise.
 */
export const DEFAULT_SETTINGS = {
    allowRegistration: true,
    passwordMinLength: 12,
    passwordRequireUppercase: false,
    passwordRequireLowercase: false,
    passwordRequireDigit: false,
    passwordRequireSymbol: false,
    maxMessageLength: 4000,
    maxDisplayNameLength: DISPLAY_NAME_MAX,
    maxChannelNameLength: CHANNEL_NAME_MAX,
    maxFileTransferBytes: DEFAULT_MAX_TRANSFER_BYTES,
    fileRetentionMinutes: 60,
    allowMessageEditing: true,
    allowMessageDeletion: true,
    allowDeleteChatForEveryone: true,
    sessionMaxLifetimeHours: 30 * 24,
    sessionIdleTimeoutHours: 0,
    loginMaxFailedAttempts: 10,
    loginLockoutMinutes: 15,
    allowedEmailDomains: [],
};
/**
 * Body of `PATCH /api/v1/admin/settings`: any subset, nothing unknown.
 *
 * Strict so a typo in a key is a 400 rather than a silent no-op that the
 * administrator believes took effect.
 */
export const updateSettingsInputSchema = z.strictObject(settingsShape).partial();
/**
 * Settings as stored, made whole.
 *
 * Stored JSON may predate a setting, or hold a value a later release tightened
 * the bounds on. Each key is validated on its own and falls back to its default,
 * so one bad value cannot take the rest of the configuration down with it.
 */
export function resolveSettings(raw) {
    const source = typeof raw === 'object' && raw !== null ? raw : {};
    const resolved = {};
    for (const key of Object.keys(settingsShape)) {
        const parsed = settingsShape[key].safeParse(source[key]);
        resolved[key] = parsed.success ? parsed.data : DEFAULT_SETTINGS[key];
    }
    return resolved;
}
/** Whether `email` may be used for self-service sign-up under `settings`. */
export function emailDomainAllowed(email, settings) {
    if (settings.allowedEmailDomains.length === 0) {
        return true;
    }
    const domain = email.trim().toLowerCase().split('@').pop() ?? '';
    return settings.allowedEmailDomains.includes(domain);
}
/**
 * Every rule the policy turns on, each with whether `password` meets it.
 *
 * One function for both ends: the registration form renders it as a live
 * checklist, and the server rejects with the unmet labels. Character classes are
 * Unicode-aware, so a Persian password is judged on its letters rather than
 * failing an ASCII-only test. Length counts code points, not UTF-16 units, so an
 * emoji is one character - which is what the person typing it sees.
 */
export function passwordRules(password, policy) {
    const rules = [
        {
            id: 'length',
            label: `At least ${policy.passwordMinLength} characters`,
            met: [...password].length >= policy.passwordMinLength,
        },
    ];
    if (policy.passwordRequireUppercase) {
        rules.push({ id: 'uppercase', label: 'An uppercase letter', met: /\p{Lu}/u.test(password) });
    }
    if (policy.passwordRequireLowercase) {
        rules.push({ id: 'lowercase', label: 'A lowercase letter', met: /\p{Ll}/u.test(password) });
    }
    if (policy.passwordRequireDigit) {
        rules.push({ id: 'digit', label: 'A digit', met: /\p{Nd}/u.test(password) });
    }
    if (policy.passwordRequireSymbol) {
        rules.push({
            id: 'symbol',
            label: 'A symbol, such as ! @ # or %',
            met: /[^\p{L}\p{N}\s]/u.test(password),
        });
    }
    return rules;
}
/** The labels of every rule `password` fails. Empty means it is acceptable. */
export function passwordPolicyIssues(password, policy) {
    return passwordRules(password, policy)
        .filter((rule) => !rule.met)
        .map((rule) => rule.label);
}
//# sourceMappingURL=settings.js.map